(() => {
    const elementName = 'aura-luxury-header';

    if (customElements.get(elementName)) {
      return;
    }

    class AuraLuxuryHeader extends HTMLElement {
      connectedCallback() {
        this.hamburger = this.querySelector(
          '[class*="aura-header__hamburger-"]'
        );

        this.closeButton = this.querySelector(
          '[class*="aura-header__close-"]'
        );

        this.drawer = this.querySelector(
          '[class*="aura-header__drawer-"]'
        );

        this.overlay = this.querySelector(
          '[class*="aura-header__overlay-"]'
        );

        this.languageButton = this.querySelector(
          '[class*="aura-header__language-button-"]'
        );

        this.languageMenu = this.querySelector(
          '[class*="aura-header__language-menu-"]'
        );

        this.hamburger?.addEventListener(
          'click',
          () => this.openDrawer()
        );

        this.closeButton?.addEventListener(
          'click',
          () => this.closeDrawer()
        );

        this.overlay?.addEventListener(
          'click',
          () => this.closeDrawer()
        );

        this.languageButton?.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            this.toggleLanguageMenu();
          }
        );

        this.querySelectorAll(
          '[class*="aura-header__drawer-link-"], [class*="aura-header__drawer-categories-"] a'
        ).forEach((link) => {
          link.addEventListener(
            'click',
            () => this.closeDrawer()
          );
        });

        this.querySelectorAll('[data-language-code]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            const form = button.closest('form');
            const languageInput = form?.querySelector('input[name="language_code"]');
            if (!languageInput) return;
            languageInput.value = button.value;
            const alternate = [...document.querySelectorAll('link[rel="alternate"][hreflang]')]
              .find((link) => link.hreflang === button.value);
            if (alternate && location.hostname.endsWith('.shopifypreview.com')) {
              const destination = new URL(alternate.href);
              destination.protocol = location.protocol;
              destination.host = location.host;
              destination.search = location.search;
              destination.hash = location.hash;
              location.assign(destination.href);
              return;
            }
            form.submit();
          });
        });

        this.headerGroup = this.closest('#header-group');
        this.measureHeader = () => {
          if (this.headerGroup) {
            const section = this.closest('.shopify-section');
            const announcementHeight = this.headerGroup.offsetHeight - (section?.offsetHeight || 0);
            this.headerGroup.style.setProperty('--aura-announcement-height', announcementHeight + 'px');
          }
        };
        this.scrollTarget = this.closest('.page-wrapper') || window;
        this.handleScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.querySelector('[class*="aura-header__sleep-"]')?.removeAttribute('open');
            this.classList.toggle('is-compact', (this.scrollTarget === window ? window.scrollY : this.scrollTarget.scrollTop) > 120);
            this.scrollFrame = null;
          });
        };
        this.headerObserver = new ResizeObserver(this.measureHeader);
        if (this.headerGroup) this.headerObserver.observe(this.headerGroup);
        this.scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
        this.measureHeader();
        this.handleScroll();

        this.handleEscape = (event) => {
          if (event.key === 'Tab' && this.drawer?.classList.contains('active')) {
            const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), summary')].filter(el => el.getClientRects().length);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }
          if (event.key === 'Escape') {
            this.querySelector('[class*="aura-header__sleep-"]')?.removeAttribute('open');
            this.closeDrawer();
            this.closeLanguageMenu();
          }
        };

        this.handleOutsideClick = (event) => {
          if (!this.querySelector('[class*="aura-header__sleep-"]')?.contains(event.target)) {
            this.querySelector('[class*="aura-header__sleep-"]')?.removeAttribute('open');
          }
          if (
            this.languageMenu &&
            !this.languageMenu.contains(event.target) &&
            !this.languageButton?.contains(event.target)
          ) {
            this.closeLanguageMenu();
          }
        };

        document.addEventListener(
          'keydown',
          this.handleEscape
        );

        document.addEventListener(
          'click',
          this.handleOutsideClick
        );
      }

      disconnectedCallback() {
        document.removeEventListener(
          'keydown',
          this.handleEscape
        );

        document.removeEventListener(
          'click',
          this.handleOutsideClick
        );

        this.headerObserver?.disconnect();
        this.scrollTarget.removeEventListener('scroll', this.handleScroll);
        cancelAnimationFrame(this.scrollFrame);
        this.closeDrawer();
      }

      openDrawer() {
        if (!this.drawer || !this.overlay) {
          return;
        }

        if (this.drawer.classList.contains('active')) return;
        this.previousOverflow = document.body.style.overflow;
        if (this.scrollTarget !== window) {
          this.previousScrollOverflow = this.scrollTarget.style.overflow;
          this.scrollTarget.style.overflow = 'hidden';
        }
        this.drawer.inert = false;
        this.drawer.setAttribute('aria-modal', 'true');
        this.drawer.classList.add('active');
        this.overlay.classList.add('active');
        this.drawer.setAttribute('aria-hidden', 'false');
        this.overlay.setAttribute('aria-hidden', 'false');
        this.hamburger?.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
        this.closeButton?.focus();
      }

      closeDrawer() {
        if (!this.drawer || !this.overlay) {
          return;
        }

        if (!this.drawer.classList.contains('active')) return;
        this.hamburger?.focus();
        this.drawer.inert = true;
        this.drawer.removeAttribute('aria-modal');
        this.drawer.classList.remove('active');
        this.overlay.classList.remove('active');
        this.drawer.setAttribute('aria-hidden', 'true');
        this.overlay.setAttribute('aria-hidden', 'true');
        this.hamburger?.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = this.previousOverflow || '';
        if (this.scrollTarget !== window) this.scrollTarget.style.overflow = this.previousScrollOverflow || '';
      }

      toggleLanguageMenu() {
        if (!this.languageMenu) {
          return;
        }

        const isOpen = this.languageMenu.classList.toggle('active');

        this.languageButton?.setAttribute(
          'aria-expanded',
          isOpen ? 'true' : 'false'
        );
      }

      closeLanguageMenu() {
        this.languageMenu?.classList.remove('active');
        this.languageButton?.setAttribute(
          'aria-expanded',
          'false'
        );
      }
    }

    customElements.define(elementName, AuraLuxuryHeader);
  })();

